import { AxDataTable } from "@/components/ax/dataTable/AxDataTable";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { ROUTES } from "@/constants/routeConstants";
import { Menu } from "@/lib/types";
import { getMenu, updateMenu } from "@/sideEffects/api";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { UpdateRowButton } from "@/components/ax/dataTable/UpdateRowButton";
import { AddRowButton } from "@/components/ax/dataTable/AddRowButton";
import { LucideIcon, LucideIconName } from "@/components/LucideIcon";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { showApiError, showSuccess } from "@/lib/toast";
import { useState } from "react";
import { AppSelect } from "@/components/ui/app-select";
import { LoadingOverlay } from "@/components/LoadingOverlay";

export const MenuPage = () => {
  const { t } = useTranslation();

  useBreadcrumbs([{ name: t("entities.menu"), path: ROUTES.MENU }]);

  const { goToMenuDetail, goToMenuAdd } = useAppNavigate();
  const [showMenuType, setShowMenuType] = useState<"admin" | "firm">("admin");

  const clientSideFilter = (rows: Menu[]) => {
    if (showMenuType === "admin") {
      return rows.filter((row) => row.adminOnly);
    }
    if (showMenuType === "firm") {
      return rows.filter((row) => !row.adminOnly);
    }
    return rows;
  };

  const queryClient = useQueryClient();

  const updateOrdersMutation = useMutation({
    mutationFn: async (newData: Menu[]) => {
      const currentData = clientSideFilter(
        (await getMenu({ numberOfObjectsPerPage: 1000, pageNumber: 1 })).data.result || []
      );

      // Create maps for easy lookup
      const currentOrderMap = new Map(currentData.map((item) => [item.id, Number(item.order)]));

      // Calculate which items actually need updates
      const itemsToUpdate = newData.filter((item, index) => {
        const newOrder = (index + 1) * 100;
        const currentOrder = currentOrderMap.get(item.id);
        return currentOrder !== newOrder;
      });

      // Only update items that changed position
      const updatePromises = itemsToUpdate.map((menu) => {
        const newOrder = (newData.findIndex((item) => item.id === menu.id) + 1) * 100;
        return updateMenu({
          id: menu.id,
          menuName: menu.menuName,
          description: menu.description ?? "",
          order: newOrder.toString(),
          menuIcon: menu.menuIcon ?? "",
          path: menu.path,
          adminOnly: menu.adminOnly ?? false,
        });
      });

      return Promise.all(updatePromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getMenu"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getUserRight"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  const columns: ColumnDef<Menu>[] = [
    {
      accessorKey: "menuName",
      header: t("common.name"),
    },
    {
      accessorKey: "menuIcon",
      header: t("menu.icon"),
      cell: ({ row }) => <LucideIcon name={row.original.menuIcon as LucideIconName} className="w-4 h-4" />,
    },
    {
      accessorKey: "path",
      header: t("menu.path"),
    },
    {
      accessorKey: "order",
      header: t("common.order"),
    },
    {
      accessorKey: "adminOnly",
      header: t("menu.adminOnly"),
      cell: ({ row }) => (
        <Badge variant={row.original.adminOnly ? "default" : "outline"}>
          {row.original.adminOnly ? t("adminOnly") : t("No")}
        </Badge>
      ),
    },
    {
      accessorKey: "id",
      header: () => "",
      cell: ({ row }) => {
        return (
          <div className="flex items-center justify-end gap-2">
            <UpdateRowButton onClickUpdate={() => goToMenuDetail(row.original.id)} />
          </div>
        );
      },
    },
  ];

  return (
    <LoadingOverlay isLoading={updateOrdersMutation.isPending}>
      <AxDataTable
        columns={columns}
        fetcher={getMenu}
        clientSideFilter={clientSideFilter}
        numberOfObjectsPerPage={1000}
        hidePageSizeSelector
        getRowId={(row) => row.id.toString()}
        uniqQueryKey="getMenu"
        onReorder={(newData) => {
          updateOrdersMutation.mutate(newData);
        }}
        extraHeader={
          <>
            <div className="w-[250px]">
              <AppSelect
                options={[
                  { label: t("menu.showAdminMenus"), value: "admin" },
                  { label: t("menu.showFirmMenus"), value: "firm" },
                ]}
                value={showMenuType}
                onChange={(value) => setShowMenuType(value as any)}
              />
            </div>
            <AddRowButton title={t("common.addNewEntity", { entity: t("entities.menu") })} onClickAdd={goToMenuAdd} />
          </>
        }
      />
    </LoadingOverlay>
  );
};
